import { useState, useEffect } from "react";
import { eventsApi } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Sparkles, Camera, FolderOpen, Film, Download, Loader2, AlertTriangle, CreditCard, QrCode
} from "lucide-react";

/**
 * PremiumUpgradeModal
 *
 * Shows a reminder popup to the HOST when the event is on the free plan and is
 * getting close to the free limit (threshold: limit - 5 photos).
 *
 * Behavior:
 *  - Opens automatically when media_count >= (free_limit - 5) AND
 *    plan === "free" AND not dismissed yet for this threshold.
 *  - Dismissed state is stored in localStorage per (eventId, threshold_bucket)
 *    so if the host adds more photos and crosses a new threshold, the popup
 *    re-opens (e.g., dismissed at 25 -> re-opens at 28 when even closer).
 *  - Host can also open manually via the parent's "Upgrade Premium" button.
 *
 * Props:
 *  - eventId (string, required)
 *  - mediaCount (number)
 *  - freeLimit (number, default 30)
 *  - plan (string: "free" | "premium")
 *  - open (optional controlled open)
 *  - onOpenChange (optional controlled setter)
 */
export default function PremiumUpgradeModal({
  eventId,
  mediaCount = 0,
  freeLimit = 30,
  plan = "free",
  open: controlledOpen,
  onOpenChange,
  reason = "quota", // "quota" (default) | "albums" - changes the header message
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMethod, setLoadingMethod] = useState(null); // "card" | "pix" | null

  const isControlled = typeof controlledOpen === "boolean";
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v);
    else setUncontrolledOpen(v);
  };

  const remaining = Math.max(freeLimit - mediaCount, 0);
  const nearThreshold = 5;
  const shouldRemind = plan === "free" && remaining > 0 && remaining <= nearThreshold;
  const alreadyReached = plan === "free" && remaining === 0;

  // Auto-open when crossing the threshold. We key the dismissal on the current
  // "remaining" bucket so each new step (5, 4, 3, 2, 1) re-opens if dismissed.
  useEffect(() => {
    if (isControlled) return; // parent controls
    if (!eventId) return;
    if (plan !== "free") return;
    if (!shouldRemind && !alreadyReached) return;

    const bucketKey = `festa_upgrade_reminder_${eventId}_r${remaining}`;
    const dismissed = typeof window !== "undefined" && localStorage.getItem(bucketKey);
    if (!dismissed) {
      setUncontrolledOpen(true);
    }
  }, [eventId, plan, mediaCount, remaining, shouldRemind, alreadyReached, isControlled]);

  const handleDismiss = () => {
    if (!isControlled && eventId) {
      try {
        const bucketKey = `festa_upgrade_reminder_${eventId}_r${remaining}`;
        localStorage.setItem(bucketKey, "1");
      } catch (_) {}
    }
    setOpen(false);
  };

  const handleUpgrade = async (method = "card") => {
    setLoading(true);
    setLoadingMethod(method);
    try {
      const originUrl = window.location.origin;
      const apiCall = method === "pix" ? eventsApi.pixCheckout : eventsApi.checkout;
      const { data } = await apiCall(eventId, originUrl);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Checkout error:", err);
      const msg = err?.response?.data?.detail || "Erro ao iniciar pagamento. Tente novamente.";
      try { alert(msg); } catch (_) {}
    }
    setLoading(false);
    setLoadingMethod(null);
  };

  // Don't render anything if plan is already premium
  if (plan === "premium") return null;

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent
        className="border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl max-w-sm mx-auto p-0 overflow-hidden"
        data-testid="premium-upgrade-modal"
      >
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-[#FF6B6B] to-[#8B5CF6] p-5 text-white">
          <DialogHeader className="space-y-0">
            <div className="flex items-center gap-2 mb-1">
              {reason === "albums" ? (
                <FolderOpen className="w-5 h-5" strokeWidth={2.5} />
              ) : reason === "download" ? (
                <Download className="w-5 h-5" strokeWidth={2.5} />
              ) : (
                <AlertTriangle className="w-5 h-5" strokeWidth={2.5} />
              )}
              <DialogTitle
                className="text-lg font-black text-white"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {reason === "albums"
                  ? "Albums sao Premium"
                  : reason === "download"
                  ? "Download e Premium"
                  : alreadyReached
                  ? "Limite atingido!"
                  : `Faltam so ${remaining} fotos!`}
              </DialogTitle>
            </div>
            <p className="text-sm text-white/90">
              {reason === "albums"
                ? "Os albums organizados por IA sao um recurso exclusivo do plano Premium. Faca upgrade agora para acessar todos os albums do seu evento."
                : reason === "download"
                ? "Baixar todas as fotos e videos do evento de uma so vez e um recurso exclusivo do plano Premium. Faca upgrade agora para levar todas as memorias da festa."
                : alreadyReached
                ? `Voce ja enviou ${mediaCount} de ${freeLimit} fotos do plano gratuito. Os convidados estao bloqueados de enviar novas fotos.`
                : `Voce ja usou ${mediaCount} de ${freeLimit} fotos do plano gratuito. Desbloqueie agora e nao tenha surpresas durante a festa.`}
            </p>
          </DialogHeader>
        </div>

        <div className="p-5">
          {/* Benefits list */}
          <p className="text-[10px] text-[#78716C] font-bold uppercase tracking-wider mb-2">
            Com o Premium voce tem:
          </p>
          <div className="space-y-2 mb-4">
            {[
              { icon: Camera, text: "Fotos e videos ilimitados" },
              { icon: FolderOpen, text: "Albums organizados por IA" },
              { icon: Film, text: "Video slideshow da festa" },
              { icon: Download, text: "Download completo do evento" },
            ].map((it) => (
              <div key={it.text} className="flex items-center gap-2">
                <div className="w-7 h-7 bg-[#FEE2E2] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shrink-0 shadow-[2px_2px_0px_#1C1917]">
                  <it.icon className="w-3.5 h-3.5 text-[#FF6B6B]" strokeWidth={2.5} />
                </div>
                <span className="text-sm text-[#1C1917] font-medium">{it.text}</span>
              </div>
            ))}
          </div>

          {/* Price box */}
          <div className="bg-[#FFFBF7] border-2 border-[#1C1917] rounded-xl p-4 text-center mb-4 shadow-[3px_3px_0px_#1C1917]">
            <p className="text-[10px] text-[#78716C] font-bold uppercase tracking-wider">
              Upgrade unico por
            </p>
            <p
              className="text-4xl font-black text-[#1C1917] mt-1 leading-none"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              R$ 9,90
            </p>
            <p className="text-[11px] text-[#78716C] mt-1">pagamento unico - sem mensalidade</p>
          </div>

          {/* CTAs - Pix (primary) + Cartão */}
          <div className="space-y-2">
            <Button
              onClick={() => handleUpgrade("pix")}
              disabled={loading}
              className="w-full bg-[#00B894] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 text-base hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all active:translate-y-[4px] active:shadow-none"
              data-testid="premium-upgrade-pix-cta"
            >
              {loadingMethod === "pix" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <QrCode className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  Pagar com Pix
                </>
              )}
            </Button>
            <Button
              onClick={() => handleUpgrade("card")}
              disabled={loading}
              className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 text-base hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all active:translate-y-[4px] active:shadow-none"
              data-testid="premium-upgrade-card-cta"
            >
              {loadingMethod === "card" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  Pagar com Cartão
                </>
              )}
            </Button>
          </div>

          {(!alreadyReached || reason === "albums" || reason === "download") && (
            <button
              onClick={handleDismiss}
              className="w-full mt-3 text-sm text-[#78716C] hover:text-[#1C1917] font-medium py-2"
              data-testid="premium-upgrade-later"
            >
              {reason === "albums" || reason === "download" ? "Voltar" : "Depois, obrigado"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
